import { Create } from '@mui/icons-material'
import AirIcon from '@mui/icons-material/Air'
import AlarmIcon from '@mui/icons-material/Alarm'
import DownIcon from '@mui/icons-material/ArrowDownward'
import UpIcon from '@mui/icons-material/ArrowUpward'
import CurtainsClosedIcon from '@mui/icons-material/CurtainsClosed'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import WindowIcon from '@mui/icons-material/Window'
import {
	IconButton,
	LinearProgress,
	Slider,
	Box,
	Tooltip,
	useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { FaFaucet, FaFaucetDrip } from 'react-icons/fa6'
import { MdOutlineCurtains } from 'react-icons/md'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import _ from 'lodash'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import EditControlForm from '../../components/ControlForm/EditControlForm'
import EditTwayControlForm from '../../components/ControlForm/EditTwayControlForm'
import EditDenjiControlForm from '../../components/ControlForm/EditDenjiControlForm'
import { CategoryIcon } from '../../components/MachineTable/MachineIcon'
import Modal from '../../components/Modal'
import TwayModeBadge from '../../components/TwayModeBadge'
import {
	deleteControl,
	updateControl,
	runTwayManual,
} from '../../store/HouseContainer/operations'
import { getRequestIsLoading } from '../../store/Network/selectors'
import { getEditControl } from '../../store/SettingContainer/selectors'
import { Control } from '../../types'
import {
	categoryStr,
	isBoolMachineCategory,
	isDenjiMachineCategory,
	isTwayMachineCategory,
} from '../../utils'
import BoolMachineToogleButton from './BoolMachineToogleButton'
import SubControlList from './SubControlList'

type OProps = {
	control: Control
	keyi: string
}

type Props = {
	control: Control
	keyi: string
	disabled: boolean
	editMode: boolean
	deleteControl: () => void
	openConfig: () => void
}

// 開度バーの左右アイコン（全閉→全開）を機器タイプ別に出し分ける
type StateIcons = {
	closed: (color: string) => React.ReactNode
	open: (color: string) => React.ReactNode
}
const RI_SIZE = 19
const mui =
	(Comp: React.ComponentType<any>) =>
	(color: string): React.ReactNode =>
		<Comp sx={{ fontSize: 24, color }} />
// react-icons v5 の IconType は React18/19 前提のため any で受ける（当リポジトリは React17 型）
const ri =
	(Comp: any) =>
	(color: string): React.ReactNode =>
		<Comp size={RI_SIZE} color={color} />

const stateIconMap: Partial<Record<string, StateIcons>> = {
	tway: { closed: ri(FaFaucet), open: ri(FaFaucetDrip) },
	window: { closed: mui(WindowIcon), open: mui(AirIcon) },
	curtain: { closed: mui(CurtainsClosedIcon), open: ri(MdOutlineCurtains) },
}
const fallbackStateIcons: StateIcons = {
	closed: mui(LockIcon),
	open: mui(LockOpenIcon),
}

const ControlRowShow = ({ control, openConfig, disabled }: Props) => {
	const dispatch = useDispatch()
	const theme = useTheme()
	const [rate, setRate] = React.useState<number>(control.rate || 50)

	// 電磁弁は開度を持たない ON/OFF 機器なので、bool 機器と同じ扱いにする
	// （ON/OFF トグルを出し、開度%バーは出さない）
	const isOnOff =
		isBoolMachineCategory(control.category) ||
		isDenjiMachineCategory(control.category)

	const stateIcons =
		(control.category && stateIconMap[control.category]) || fallbackStateIcons

	const handleExecute = () => {
		dispatch(runTwayManual(control.id, rate))
	}

	return (
		<div style={{ width: '100%' }}>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						flex: 1,
						minWidth: 0,
					}}
				>
					<CategoryIcon
						category={control.category}
						stepRate={6}
						style={{ width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
					/>
					<Typography
						variant="subtitle1"
						sx={{
							fontWeight: 600,
							minWidth: 0,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{control.label}
					</Typography>
					<Chip
						size="small"
						variant="outlined"
						label={categoryStr[control.category]}
						sx={{
							height: 18,
							flexShrink: 0,
							'& .MuiChip-label': { px: 0.75, fontSize: 10 },
						}}
					/>
				</div>
				<IconButton size="small" onClick={() => openConfig()}>
					<Create />
				</IconButton>
				{!isTwayMachineCategory(control.category) && control.timer ? (
					<Chip
						size="small"
						icon={<AlarmIcon />}
						label={`${control.timer}秒`}
						sx={{ mx: 0.5, flexShrink: 0 }}
					/>
				) : null}
				{isOnOff && <BoolMachineToogleButton control={control} />}
			</div>

			{!isOnOff && (
				<>
					{/* 現在の状態（読み取り専用の表示） */}
					<Box sx={{ mt: 0.5, width: { xs: '100%', sm: '50vw' } }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
							<Typography variant="caption" color="text.secondary">
								現在の状態
							</Typography>
							{isTwayMachineCategory(control.category) && (
								<TwayModeBadge control={control} />
							)}
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Tooltip title="全閉">
								<Box
									component="span"
									sx={{ display: 'inline-flex', lineHeight: 0 }}
								>
									{stateIcons.closed(theme.palette.text.disabled)}
								</Box>
							</Tooltip>
							<LinearProgress
								variant="determinate"
								value={control.rate}
								sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
							/>
							<Tooltip title="全開">
								<Box
									component="span"
									sx={{ display: 'inline-flex', lineHeight: 0 }}
								>
									{stateIcons.open(theme.palette.primary.main)}
								</Box>
							</Tooltip>
							<Typography
								variant="body2"
								sx={{ minWidth: 52, textAlign: 'right' }}
							>
								{control.rate}%開
							</Typography>
						</Box>
					</Box>

					{isTwayMachineCategory(control.category) && (
						<Box
							sx={{
								mt: 2,
								p: 2,
								width: { xs: '100%', sm: '50vw' },
								border: '1px dashed',
								borderColor: 'divider',
								borderRadius: 1,
								bgcolor: 'action.hover',
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: 'block', mb: 1 }}
							>
								手動操作（開度を指定して実行）
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Box sx={{ flex: 1, mr: 2 }}>
									<Typography variant="caption" color="text.secondary">
										指定開度: {rate}%
									</Typography>
									<Slider
										value={rate}
										onChange={(_, value) => setRate(value as number)}
										min={0}
										max={100}
										step={10}
										marks
										disabled={disabled}
										valueLabelDisplay="auto"
									/>
								</Box>
								<Button
									variant="contained"
									color="primary"
									onClick={handleExecute}
									disabled={disabled}
								>
									実行
								</Button>
							</Box>
						</Box>
					)}
				</>
			)}
		</div>
	)
}

const ControlRowEdit = (props: Props) => {
	return (
		<>
			<div style={{ flex: 1, display: 'flex' }}>
				<Typography variant="h6" style={{ paddingLeft: '0', flex: 1 }}>
					{props.control.label}
				</Typography>
			</div>
			{props.control.activePinView && (
				<Chip
					avatar={
						<Avatar>
							<UpIcon />
						</Avatar>
					}
					label={props.control.activePinView}
				/>
			)}
			{props.control.negativePinView && (
				<Chip
					avatar={
						<Avatar>
							<DownIcon />
						</Avatar>
					}
					label={props.control.negativePinView}
				/>
			)}
			<Button
				disabled={props.disabled}
				variant="outlined"
				color="error"
				size="small"
				onClick={props.deleteControl}
				data-test={`control-del-btn-${props.keyi}`}
			>
				削除する
			</Button>
		</>
	)
}

const ControlRowBase = (props: Omit<Props, 'openConfig'>) => {
	const [open, setOpen] = React.useState(false)
	const dispath = useDispatch()
	const { control } = props
	// 三方弁はピン状態(isHit)で ON 表現しない
	const isOn = !isTwayMachineCategory(control.category) && control.isHit

	return (
		<>
			<ListItem
				data-test={`control-item-${control.id}`}
				sx={{
					borderTop: '1px solid',
					borderTopColor: 'divider',
					borderLeft: '3px solid',
					borderLeftColor: isOn ? 'success.main' : 'transparent',
					bgcolor: isOn
						? (t) => alpha(t.palette.success.main, 0.1)
						: 'transparent',
					py: 0.75,
					pr: 0,
					pl: 1,
				}}
			>
				{props.editMode ? (
					<ControlRowEdit {...props} openConfig={() => setOpen(true)} />
				) : (
					<ControlRowShow {...props} openConfig={() => setOpen(true)} />
				)}
			</ListItem>
			{!isBoolMachineCategory(props.control.category) &&
				!isDenjiMachineCategory(props.control.category) && (
					<SubControlList control={props.control} keyi={props.keyi} />
				)}
			<Modal open={open} closeModal={() => setOpen(false)}>
				{isTwayMachineCategory(props.control.category) ? (
					<EditTwayControlForm
						disabled={props.disabled}
						initialValues={{
							..._.omit(control, ['id']),
							activePin: control.activePin + 1 || null,
							negativePin: control.negativePin + 1 || null,
						}}
						handleSubmit={(v) => {
							dispath(
								updateControl(
									{
										...v,
										houseId: control.houseId,
									},
									control.id,
								),
							)
							setOpen(false)
						}}
					/>
				) : isDenjiMachineCategory(props.control.category) ? (
					<EditDenjiControlForm
						disabled={props.disabled}
						initialValues={{
							..._.omit(control, ['id']),
							activePin: control.activePin + 1 || null,
							negativePin: control.negativePin + 1 || null,
						}}
						handleSubmit={(v) => {
							dispath(
								updateControl(
									{
										...v,
										houseId: control.houseId,
									},
									control.id,
								),
							)
							setOpen(false)
						}}
					/>
				) : (
					<EditControlForm
						disabled={props.disabled}
						initialValues={{
							..._.omit(control, ['id']),
							activePin: control.activePin + 1 || null,
							negativePin: control.negativePin + 1 || null,
						}}
						handleSubmit={(v) => {
							dispath(
								updateControl(
									{
										...v,
										houseId: control.houseId,
									},
									control.id,
								),
							)
							setOpen(false)
						}}
					/>
				)}
			</Modal>
		</>
	)
}

function ControlRow(props: OProps) {
	const disabled = useSelector(getRequestIsLoading)
	const editMode = useSelector(getEditControl)
	const dispatch = useDispatch()

	return (
		<ControlRowBase
			{...props}
			disabled={disabled}
			editMode={editMode}
			deleteControl={() => dispatch(deleteControl(props.control))}
		/>
	)
}

export default ControlRow
